import { Content, ContentType, ProductError, ProductErrorType } from '../../models';

/**
 * ## Représente l'intégralité du contenu d'un JumpForm
 *
 * Le JumpForm est organisé en "slides" et la liste des slides est obtenue à partir de la liste des interactivités.
 *
 * ex:
 * ```
 * "game": {
 *   "interactivities": [
 *     {
 *       "type": "presentation",
 *       "chapter": "Introduction",
 *       "presentationData": {...}
 *     }, {
 *       "type": "presentation",
 *       "chapter": "Connaissance à acquérir",
 *       "presentationData": {...}
 *     }, {
 *       "type": "quiz",
 *       "chapter": "Connaissance à acquérir",
 *       "quizData": {...}
 *     }, {
 *       "type": "presentation",
 *       "chapter": "Accompagner le client pour plus d'autonomie",
 *       "presentationData": {...}
 *     }, {
 *       "type": "quiz",
 *       "chapter": "Accompagner le client pour plus d'autonomie",
 *       "quizData": {...}
 *     }, {
 *       "type": "presentation",
 *       "chapter": "Conclusion",
 *       "presentationData": {...}
 *     }
 *   ]
 * }
 * ```
 */
export class JumpFormData {
  /**
   * Autorise la modification de toutes les réponses jusqu'à soumission du JumpForm
   *
   * N'est pas compatible avec le fait d'afficher les réponses immédiatement
   *
   * Est considéré comme faux si non présent
   */
  canModify?: boolean;

  /**
   * Liste des interactivités qui composent le JumpForm.
   *
   * **L'ordre des interactivités est important:** une interactivité peut générer une ou plusieurs slides mais l'ordre des groupes
   * de slides obtenus respectera toujours l'ordre de déclaration des interactivités.
   */
  interactivities: Interactivity[];

  constructor(formData?: JumpFormData) {
    this.canModify = formData?.canModify || false;
    this.interactivities = (formData?.interactivities || []).map(i => new Interactivity(i));
  }

  static validate(jumpForm: JumpFormData): ProductError[] {
    const errors = [];
    if (!jumpForm?.interactivities?.length) {
      errors.push({ type: ProductErrorType.MISSING, property: 'interactivities' });
    } else {
      jumpForm.interactivities
        .map((interactivity, i) => Interactivity.validate(interactivity, i))
        .reduce((flattenedArr, subArray) => flattenedArr.concat(subArray), [])
        .forEach(e => errors.push(e));
    }
    return errors;
  }
}

/**
 * ## Une interactivité peut générer une ou plusieurs slides.
 *
 * Les slides générées dépendent du type de l'interactivité (voir [[InteractivityType]]).
 *
 * Ex:
 * ```
 * {
 *   "type": "presentation",
 *   "chapter": "Chapitre 1",
 *   "presentationData": {...},
 *   "quizData": {...}
 * }
 * ```
 */
export class Interactivity {
  slug: string;
  /**
   * presentationData, quizData, ... dépendent du type.
   *
   * Le type impose quel `data` est présent. Seul le data correspondant est présent
   */
  type: InteractivityType;

  /**
   * Le chapitre est affiché sur toutes les slides correspondante.
   *
   * Pour les quiz, toutes les questions d'un même quiz partage le même nom de chapitre.
   * La numérotation de des questions est réinitialisée entre 2 chapitres
   *
   * Si 2 quiz doivent partager la numérotation, il faut alors qu'ils aient le même nom de chapitre
   */
  chapter: string;

  /**
   * Données nécessaire pour les slides de présentation (simple slide de contenu: texte audio, vidéo ou image)
   */
  presentationData?: PresentationData;

  /**
   * Données de configuration d'un quiz
   */
  quizData?: QuizData;

  /**
   * Données de configuration d'une enquête
   */
  surveyData?: SurveyData;

  constructor(interactivity: Interactivity) {
    this.slug = interactivity.slug;
    this.type = interactivity.type;
    this.chapter = interactivity.chapter;
    switch (interactivity.type) {
      case InteractivityType.PRESENTATION:
        this.presentationData = new PresentationData(interactivity.presentationData);
        break;
      case InteractivityType.QUIZ:
        this.quizData = new QuizData(interactivity.quizData);
        break;
      case InteractivityType.SURVEY:
        this.surveyData = new SurveyData(interactivity.surveyData);
        break;
    }
  }

  static validate(interactivity: Interactivity, interactivityIndex?: number): ProductError[] {
    const errors = [];
    if (!interactivity.chapter && interactivity.type !== InteractivityType.QUIZ) {
      errors.push({ type: ProductErrorType.MISSING, property: 'chapter', payload: { interactivityIndex } });
    }
    if (!interactivity.type) {
      errors.push({ type: ProductErrorType.MISSING, property: 'type', payload: { interactivityIndex } });
    }
    switch (interactivity.type) {
      case InteractivityType.PRESENTATION:
        PresentationData.validate(interactivity.presentationData, interactivityIndex).forEach(e => errors.push(e));
        break;
      case InteractivityType.QUIZ:
        QuizData.validate(interactivity.quizData, interactivityIndex).forEach(e => errors.push(e));
        break;
      case InteractivityType.SURVEY:
        SurveyData.validate(interactivity.surveyData, interactivityIndex).forEach(e => errors.push(e));
        break;
    }
    return errors;
  }
}

/**
 * ## Énumération des type d'interactivités possible
 *
 * **Attention: les valeurs à saisir dans le JSON doivent être en minuscule**
 */
export enum InteractivityType {
  /**
   * Simple slide de contenu: texte audio, vidéo ou image (voir [[Content]] et [[PresentationData]])
   */
  PRESENTATION = 'presentation',

  /**
   * Ensemble de slides de formant un quiz (voir [[QuizData]])
   */
  QUIZ = 'quiz',

  /**
   * Ensemble de slides de formant une enquête (voir [[SurveyData]])
   */
  SURVEY = 'survey',
}

/**
 * ## Contenu d'une slide simple
 *
 * Contenu possible: texte audio, vidéo ou image (voir [[Content]])
 */
export class PresentationData {
  /**
   * Liste de contenu présent dans la slide (voir [[Content]])
   */
  contents: Content[];

  /** @ignore */
  constructor(rawObject: PresentationData) {
    this.contents = (rawObject.contents || []).map(c => new Content(c));
  }

  static validate(presentationData: PresentationData, interactivityIndex: number): ProductError[] {
    const errors: ProductError[] = [];
    if (!presentationData.contents?.length) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'contents',
        payload: { interactivityIndex, data: 'presentationData' },
      });
      return errors;
    }
    return presentationData.contents
      .map((c, i) => Content.validate(c, i, { interactivityIndex, data: 'presentationData' }))
      .reduce((flattened, contentError) => flattened.concat(contentError), errors);
  }
}

/**
 * ## Décris un quiz.
 *
 * Un quiz génère plusieurs slides de questions tiré (potentiellement au hasard) parmis le pool de questions.
 *
 * Ex:
 * ```
 * {
 *   "type": "quiz",
 *   "chapter": "Chapitre 1",
 *   "quizData": {
 *     "configuration": {
 *       "nbQuestion": 5,
 *       "previousStepsEditable": true,
 *       "constraints": {...}
 *     },
 *     "questions": [
 *       {
 *         "id":0,
 *         "contents": [...],
 *         "type": "single",
 *         "group": "EASY",
 *         "tags": ["general"],
 *         "correct": "\*\*Nickel\*\* !",
 *         "wrong": "Oups ... \*\*quelques notions ne sont pas maîtrisée\*\* !",
 *         "possibilities" : [
 *           { "text": "Réponse A", "correct": false },
 *           { "text": "Réponse B", "correct": false },
 *           { "text": "Réponse C", "correct": false },
 *           { "text": "Réponse D", "correct": true }
 *         ]
 *       },
 *       {...}, {...}, {...}, {...}, {...}, {...}, {...}, {...}, {...}
 *     ]
 *   }
 * }
 * ```
 */
export class QuizData {
  /**
   * Configure le quiz.
   *
   * Permet de choisir si les questions sont toutes prises dans l'ordre, si elles sont tirées au hasard,
   * s'il y a des contraintes particulières (prendre au moins 5 questions faciles, ...)
   */
  configuration?: QuizConfiguration;

  /**
   * Pool de toutes les question possible
   *
   * Dans le cas de tirage aléatoir avec contrainte, il est impératif que ce pool contiennent suffisamment de question.
   */
  questions: QuizQuestion[];

  constructor(quizData: QuizData) {
    this.configuration = new QuizConfiguration(quizData.configuration);
    this.questions = (quizData.questions || []).map(q => new QuizQuestion(q));
  }

  static validate(quizData: QuizData, interactivityIndex: number): ProductError[] {
    const errors: ProductError[] = [];
    if (quizData.configuration) {
      QuizConfiguration.validate(quizData.configuration, quizData.questions.length, {
        data: 'quizData.configuration',
        interactivityIndex,
      }).forEach(e => errors.push(e));
    }
    if (!quizData.questions?.length) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'question',
        payload: { interactivityIndex, data: 'quizData' },
      });
      return errors;
    }
    return quizData.questions
      .map((q, questionIndex) =>
        QuizQuestion.validate(q, { interactivityIndex, questionIndex, data: 'presentationData' }),
      )
      .reduce((flattened, subErrors) => flattened.concat(subErrors), errors);
  }
}

/**
 * ## Informations de configuration permettant de construire un quiz
 *
 * Ex:
 * ```
 * "configuration": {
 *   "nbQuestion": 5,
 *   "previousStepsEditable": true,
 *   "constraints": {
 *     "byGroup": [
 *       { "slug": "EASY",   "nbMin": 5, "nbMax": 5 },
 *       { "slug": "MEDIUM", "nbMin": 5, "nbMax": 5 },
 *       { "slug": "HARD",   "nbMin": 5, "nbMax": 5 }
 *     ],
 *     "byTag": [
 *       { "slug": "general", "nbMin": 5, "nbMax": 5 },
 *       { "slug": "s1",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s2",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s3",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s4",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s5",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s6",      "nbMin": 1, "nbMax": null },
 *       { "slug": "s7",      "nbMin": 1, "nbMax": null }
 *     ]
 *   }
 * },
 * ```
 */
export class QuizConfiguration {
  /**
   * Nombre de question à prendre dans le quiz (n'a pas de sens si l'on applique pas de tirages aléatoire)
   */
  nbQuestion?: number;
  hasNbQuestion?: boolean;

  /**
   * Si `true` concerve l'ordre des question
   * par défaut l'ordre est aléatoire
   */
  keepOrder?: boolean;

  /**
   * Affiche une animation avant chaque question
   * (pour la roue de la fortune)
   */
  showAnimation?: boolean;
  verticalAnimationBackgroundSrc?: string;
  horizontalAnimationBackgroundSrc?: string;

  /**
   * Liste toutes les contraintes par critères (ex: "Maximum 3 avec le tag 'animation'" voir [[Constraint]])
   */
  constraints?: { byGroup: Constraint[]; byTag: Constraint[] };

  constructor(config?: QuizConfiguration) {
    this.nbQuestion = config?.nbQuestion;
    this.keepOrder = config?.keepOrder ?? true;
    this.hasNbQuestion = config?.hasNbQuestion ?? (!this.keepOrder && !!this.nbQuestion);
    this.showAnimation = config?.showAnimation ?? false;
    this.constraints = {
      byGroup: (config?.constraints?.byGroup || []).map(g => new Constraint(g)),
      byTag: (config?.constraints?.byTag || []).map(g => new Constraint(g)),
    };
    this.verticalAnimationBackgroundSrc = config?.verticalAnimationBackgroundSrc;
    this.horizontalAnimationBackgroundSrc = config?.horizontalAnimationBackgroundSrc;
  }

  static validate(configuration: QuizConfiguration, nbQuestion: number, payload: any): ProductError[] {
    const errors: ProductError[] = [];
    if (configuration?.constraints?.byGroup?.some(c => !c.slug)) {
      errors.push({ type: ProductErrorType.MISSING, property: 'constraints.byGroup[...].slug', payload });
    }
    if (configuration?.constraints?.byTag?.some(c => !c.slug)) {
      errors.push({ type: ProductErrorType.MISSING, property: 'constraints.byTag[...].slug', payload });
    }
    if (configuration.hasNbQuestion && !configuration.nbQuestion && configuration.nbQuestion !== 0) {
      errors.push({ type: ProductErrorType.MISSING, property: 'nbQuestion', payload });
    } else if (
      configuration.hasNbQuestion &&
      (configuration.nbQuestion <= 0 || configuration.nbQuestion > nbQuestion)
    ) {
      errors.push({ type: ProductErrorType.OUT_OF_RANGE, property: 'nbQuestion', payload });
    }
    return errors;
  }
}

/**
 * Décrit une contrainte sur le nombre de question ayant un groupe particulier ou un tag particulier
 */
export class Constraint {
  /**
   * Nom de l'élément (group ou tag voir [[QuizQuestion]])
   */
  slug: string;

  /**
   * contrainte de minimum de présence.
   *
   * `null` est accépté et est équivalent à 0.
   */
  nbMin?: number;

  /**
   * contrainte de maximum de présence.
   *
   * `null` est accépté et est équivalent à _infini_.
   */
  nbMax?: number;

  constructor(constraint?: Constraint) {
    this.slug = constraint?.slug;
    this.nbMax = constraint?.nbMax;
    this.nbMin = constraint?.nbMin;
  }
}

/**
 * ## Question du quiz.
 *
 * Ex:
 * ```
 * {
 *   "id":0,
 *   "contents": [...],
 *   "type": "single",
 *   "group": "EASY",
 *   "tags": ["general"],
 *   "correct": "\*\*Nickel\*\* !",
 *   "wrong": "Oups ... \*\*quelques notions ne sont pas maîtrisée\*\* !",
 *   "possibilities" : [
 *     { "text": "Réponse A", "correct": false },
 *     { "text": "Réponse B", "correct": false },
 *     { "text": "Réponse C", "correct": false },
 *     { "text": "Réponse D", "correct": true }
 *   ]
 * }
 * ```
 */
export class QuizQuestion {
  /**
   * Identifiant unique de la question.
   *
   * Permet l'identification de la question dans le quiz par une plateforme pour facilité l'analyse des résultats
   */
  id?: number;

  /**
   * Titre de la question
   *
   */
  title?: string;

  /**
   * Contenu de la question
   *
   * Peut prendre la forme de: texte, audio, vidéo ou image (voir [[Content]] et [[PresentationData]])
   */
  contents: Content[];

  /**
   * Précise le type de question, choix simple, choix multiple, ...
   */
  type: QuizQuestionType;

  /**
   * Permet de regrouper les questions, par exemple, par difficulté
   *
   * Est aussi utilisé pour les contrainte de configuration (voir [[QuizConfiguration]])
   */
  group?: string;

  /**
   * Permet d'ajouter de la contextualisation aux questions
   *
   * Est aussi utilisé pour les contrainte de configuration (voir [[QuizConfiguration]])
   */
  tags?: string[];

  /**
   * Phrase d'explication/félicitation lors de la correction si la réponse est correct
   */
  correct?: string;

  /**
   * Phrase d'explication lors de la correction si la réponse est fausse
   */
  wrong?: string;

  /**
   * Pondération appliqué à la question lors du calcul du score
   */
  weight?: number;

  /**
   * Type de réponse acceptée
   */
  answerType?: ContentType.TEXT | ContentType.IMAGE;

  /**
   * Un indice est présent ou non sur la question
   */
  hasClue?: boolean;

  /**
   * Indice lié à la question
   */
  clue?: string;

  /**
   * Liste des possibilités offertes au joueur pour répondre à la question
   */
  possibilities: QuizQuestionPossibility[];

  /** @ignore */
  constructor(quizQuestion: QuizQuestion) {
    this.id = quizQuestion.id;
    this.contents = (quizQuestion.contents || []).map(c => new Content(c));
    this.type = quizQuestion.type;
    this.group = quizQuestion.group;
    this.title = quizQuestion.title || '';
    this.tags = quizQuestion.tags ? [...quizQuestion.tags] : [];
    this.answerType = quizQuestion.answerType ?? ContentType.TEXT;
    this.hasClue = quizQuestion.hasClue ?? false;
    this.clue = quizQuestion.clue;
    this.correct = quizQuestion.correct;
    this.wrong = quizQuestion.wrong;
    this.weight = quizQuestion.weight ?? 1;
    this.possibilities = (quizQuestion.possibilities || []).map(p => new QuizQuestionPossibility(p));
  }

  static validate(question: QuizQuestion, payload: any = {}): ProductError[] {
    const errors: ProductError[] = [];
    if (!question.type) {
      errors.push({ type: ProductErrorType.MISSING, property: 'type', payload });
    }

    if (question.hasClue && !question.clue?.length) {
      errors.push({ type: ProductErrorType.CANNOT_BE_NULL, property: 'clue', payload });
    }

    if (!question.contents.length) {
      errors.push({ type: ProductErrorType.CANNOT_BE_NULL, property: 'content', payload });
    }

    if (question.contents?.length) {
      question.contents
        .map((c, i) => Content.validate(c, i, payload))
        .forEach(contentErrors => errors.push(...contentErrors));
    }

    if (!question.possibilities?.length) {
      errors.push({ type: ProductErrorType.MISSING, property: 'possibilities', payload });
    } else {
      question.possibilities
        .map((p, possibilityIndex) =>
          QuizQuestionPossibility.validate(question.answerType, p, { ...payload, possibilityIndex }),
        )
        .forEach(contentErrors => errors.push(...contentErrors));
      if (question.possibilities.every(p => !p.correct)) {
        errors.push({ type: ProductErrorType.NOT_ENOUGH_VALUE, property: 'possibilities.correct', payload });
      }
      if (question.possibilities.length < 2) {
        errors.push({ type: ProductErrorType.NOT_ENOUGH_VALUE, property: 'possibilities', payload });
      }
    }
    return errors;
  }
}

/**
 * ## Énumération des type d'interactivités possible
 *
 * **Attention: les valeurs à saisir dans le JSON doivent être en minuscule**
 */
export enum QuizQuestionType {
  /**
   * Question à choix simple
   */
  SINGLE = 'single',

  /**
   * Question à choix multiple
   * Pour le calcul des scores, il faut que la réponse soit exacte pour avoir tous les points
   */
  MULTIPLE = 'multiple',
}

/**
 * Possibilité offerte au joueur pour répondre à une question (voir [[QuizQuestion]] && [[SurveyData]])
 */
export class QuestionPossibility {
  /**
   * Libellé de la possibilité
   */
  text: string;

  /**
   * Permet d'afficher des informations complémentaires lors de la correction
   * seules les réponses choisies affichent leurs compléments
   */
  moreInfo?: string;

  /**
   * Champ uniquement utilisé côté code, ne doit pas être mis dans le JSON initialement
   * Permetterai de mettre une réponse présélectionnée
   * @ignore
   */
  isSelected?: boolean;

  /**
   * Des mots clés ajouté à la proposition, ils ne sont utiles qu'en cas de post-traitements
   */
  tags?: string[];

  /** @ignore */
  constructor(quizQuestionPossibility: QuestionPossibility) {
    this.text = quizQuestionPossibility.text;
    this.moreInfo = quizQuestionPossibility.moreInfo;
    this.isSelected = quizQuestionPossibility.isSelected || false;
    this.tags = (quizQuestionPossibility?.tags || []).map(v => v);
  }

  static validate(possibility: QuestionPossibility, payload: any): ProductError[] {
    return !possibility.text ? [{ type: ProductErrorType.MISSING, property: 'text', payload }] : [];
  }
}

/**
 * Possibilité offerte au joueur pour répondre à une question (voir [[QuizQuestion]])
 */
export class QuizQuestionPossibility {
  /**
   * Libellé de la possibilité
   */
  text: string;

  /**
   * Permet d'afficher des informations complémentaires lors de la correction
   * seules les réponses choisies affichent leurs compléments
   */
  moreInfo?: string;

  /**
   * Champ uniquement utilisé côté code, ne doit pas être mis dans le JSON initialement
   * Permetterai de mettre une réponse présélectionnée
   * @ignore
   */
  isSelected?: boolean;

  /**
   * Des mots clés ajouté à la proposition, ils ne sont utiles qu'en cas de post-traitements
   */
  tags?: string[];

  /**
   * Permet de spécifier si cette possibilité est:
   *  * `true`: une bonne réponse
   *  * `false`: une fausse réponse
   *  * `null`: sans importance, la cocher ou non, n'impacte pas le score
   */
  correct: boolean;

  /**
   * Source de l'image si présente
   */
  src?: string;

  /** @ignore */
  constructor(quizQuestionPossibility: QuizQuestionPossibility) {
    this.text = quizQuestionPossibility.text;
    this.moreInfo = quizQuestionPossibility.moreInfo;
    this.isSelected = quizQuestionPossibility.isSelected || false;
    this.tags = (quizQuestionPossibility?.tags || []).map(v => v);
    this.correct = quizQuestionPossibility.correct;
    this.src = quizQuestionPossibility?.src;
  }

  static validate(
    questionType: ContentType.IMAGE | ContentType.TEXT,
    possibility: QuizQuestionPossibility,
    payload: any,
  ): ProductError[] {
    let property = '';
    if (questionType === ContentType.TEXT && !possibility.text) {
      property = 'text';
    } else if (questionType === ContentType.IMAGE && !possibility.src) {
      property = 'src';
    }
    return property.length ? [{ type: ProductErrorType.MISSING, property, payload }] : [];
  }
}

export class SurveyData {
  /**
   * Contenu de la question
   *
   * Peut prendre la forme de: texte, audio, vidéo ou image (voir [[Content]])
   */
  contents: Content[];
  /**
   * Permet de déterminer quel type de réponse est à utiliser
   */
  answerType: AnswerType;
  /**
   * Réponse libre
   */
  freeAnswer?: string;
  /**
   * Réponses prédéfini
   */
  possibilities?: QuestionPossibility[];
  /**
   * Notation définie par l'utilisateur
   */
  likertTable?: LikertTable;
  /**
   * Notation définie par l'utilisateur
   */
  satisfaction?: Satisfaction;

  constructor(surveyData?: SurveyData) {
    this.answerType = surveyData?.answerType;
    this.contents = (surveyData?.contents || []).map(c => new Content(c));
    switch (this.answerType) {
      case AnswerType.FREE:
        this.freeAnswer = surveyData?.freeAnswer;
        break;
      case AnswerType.SATISFACTION:
        this.satisfaction = new Satisfaction(surveyData?.satisfaction);
        break;
      case AnswerType.LIKERT:
        this.likertTable = new LikertTable(surveyData?.likertTable);
        break;
      case AnswerType.SINGLE:
      case AnswerType.MULTIPLE:
        this.possibilities = surveyData?.possibilities;
        break;
    }
  }

  static validate(surveyData: SurveyData, interactivityIndex?: number): ProductError[] {
    const errors: ProductError[] = [];
    if (!surveyData.answerType) {
      errors.push({
        type: ProductErrorType.MISSING,
        property: 'title',
        payload: { interactivityIndex, data: 'surveyData' },
      });
    }
    switch (surveyData.answerType) {
      case AnswerType.SINGLE:
      case AnswerType.MULTIPLE:
        (surveyData.possibilities || [])
          .map((p, possibilityIndex) =>
            QuestionPossibility.validate(p, { interactivityIndex, possibilityIndex, data: 'surveyData' }),
          )
          .forEach(contentErrors => errors.push(...contentErrors));
        break;
      case AnswerType.LIKERT:
        if (surveyData.likertTable) {
          LikertTable.validate(surveyData.likertTable, {
            interactivityIndex,
            data: 'surveyData',
          }).forEach(contentErrors => errors.push(contentErrors));
        } else {
          errors.push({
            type: ProductErrorType.MISSING,
            property: 'likertTable',
            payload: { interactivityIndex, data: 'surveyData' },
          });
        }
        break;
      case AnswerType.SATISFACTION:
        if (
          !surveyData.satisfaction?.scale ||
          surveyData.satisfaction?.scale < 3 ||
          surveyData.satisfaction?.scale > 10
        ) {
          errors.push({
            type: ProductErrorType.OUT_OF_RANGE,
            property: 'satisfaction.scale',
            payload: { interactivityIndex, data: 'surveyData' },
          });
        }
        break;
    }
    if (!surveyData.contents?.length) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'contents',
        payload: { interactivityIndex, data: 'surveyData' },
      });
    }
    (surveyData.contents || [])
      .map((c, i) => Content.validate(c, i, { interactivityIndex, data: 'surveyData' }))
      .forEach(contentErrors => errors.push(...contentErrors));
    return errors;
  }
}

export class LikertTable {
  rows: LikertRow[];
  columns: string[];

  constructor(table?: LikertTable) {
    this.rows = (table?.rows || []).map(r => new LikertRow(r));
    this.columns = table?.columns || [];
  }

  static validate(likertTable: LikertTable, payload: any): ProductError[] {
    const errors: ProductError[] = [];
    likertTable.rows
      .map((r, rowIndex) => LikertRow.validate(r, { ...payload, rowIndex, data: 'LikertTable' }))
      .forEach(contentErrors => errors.push(...contentErrors));
    if (likertTable.rows?.length > 10) {
      errors.push({
        type: ProductErrorType.TOO_MUCH_VALUES,
        property: 'rows',
        payload: { ...payload, data: 'LikertTable' },
      });
    }
    if (likertTable.columns?.length < 3) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'columns',
        payload: { ...payload, data: 'LikertTable' },
      });
    }
    if (likertTable.columns?.length > 7) {
      errors.push({
        type: ProductErrorType.TOO_MUCH_VALUES,
        property: 'columns',
        payload: { ...payload, data: 'LikertTable' },
      });
    }
    return errors;
  }
}

export class LikertRow {
  question: string;
  selectedValue: string;

  constructor(row?: LikertRow) {
    this.question = row?.question || '';
    this.selectedValue = row?.selectedValue;
  }

  static validate(likertRow: LikertRow, payload: any): ProductError[] {
    const errors: ProductError[] = [];
    if (!likertRow.question) {
      errors.push({
        type: ProductErrorType.MISSING,
        property: 'question',
        payload: { ...payload, data: 'LikertRow' },
      });
    }
    return errors;
  }
}

export class Satisfaction {
  scale: number;
  selectedValue?: number;

  constructor(column?: Satisfaction) {
    this.scale = column?.scale || 0;
    this.selectedValue = column?.selectedValue || 0;
  }

  static validate(satisfaction: Satisfaction, payload: any): ProductError[] {
    const errors: ProductError[] = [];
    if (!satisfaction.scale) {
      errors.push({
        type: ProductErrorType.MISSING,
        property: 'question',
        payload: { ...payload, data: 'LikertRow' },
      });
    }
    if (satisfaction.scale > 10) {
      errors.push({
        type: ProductErrorType.OUT_OF_RANGE,
        property: 'scale',
        payload: { ...payload, data: 'Satisfaction' },
      });
    }
    return errors;
  }
}

export enum AnswerType {
  FREE = 'free',
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  SATISFACTION = 'satisfaction',
  LIKERT = 'likert',
}
